import { defaultServiceName } from "./appConfig.js";
import { formatTokenUnits } from "./marketTokens.js";

export const MARKET_OG_WIDTH = 1200;
export const MARKET_OG_HEIGHT = 600;
export const MARKET_OG_CACHE_CONTROL = "public, max-age=31536000, immutable";
export const MARKET_OG_LIVE_CACHE_CONTROL = "public, max-age=60";
export const MARKET_OG_UPCOMING_CACHE_CONTROL = "public, max-age=300";
export const MARKET_OG_SETTLED_CACHE_CONTROL = "public, max-age=31536000, immutable";
export const MARKET_OG_RENDERER_VERSION = "v3";

const textEncoder = new TextEncoder();

export function buildMarketOgStorageKey({ schema, marketId, rendererVersion = MARKET_OG_RENDERER_VERSION }) {
  const safeRendererVersion = normalizeRendererVersion(rendererVersion);
  const safeSchema = normalizeSchema(schema);
  const safeMarketId = String(marketId || "").trim();
  if (!safeMarketId) {
    throw new Error("Market OG storage key requires a market id.");
  }
  if (!/^[A-Za-z0-9_-]+(?:-[A-Za-z0-9_-]+)*$/.test(safeMarketId)) {
    throw new Error("Market OG storage key market id contains unsupported characters.");
  }
  return `market-og/${safeRendererVersion}/${safeSchema}/${safeMarketId}.png`;
}

export function buildMarketOgPublicUrl({ env = {}, storageKey }) {
  const imageOrigin = resolveMarketOgPublicOrigin(env);
  const safeStorageKey = String(storageKey || "").trim().replace(/^\/+/, "");
  if (!safeStorageKey) {
    throw new Error("Market OG public URL requires a storage key.");
  }
  return `${imageOrigin}/${safeStorageKey}`;
}

export function buildDynamicMarketOgPath({ schema, marketId, rendererVersion = MARKET_OG_RENDERER_VERSION }) {
  const safeRendererVersion = normalizeRendererVersion(rendererVersion);
  const safeSchema = normalizeSchema(schema);
  const safeMarketId = normalizeMarketId(marketId);
  return `/market-og/${safeRendererVersion}/${safeSchema}/${safeMarketId}.png`;
}

export function buildDynamicMarketOgPublicUrl({ env = {}, schema, marketId }) {
  return `${resolveMarketOgPublicOrigin(env)}${buildDynamicMarketOgPath({ schema, marketId })}`;
}

export function chooseMarketOgCacheControl({ status } = {}) {
  const normalized = String(status || "").toLowerCase();
  if (["closed", "ended", "settled", "final"].includes(normalized)) return MARKET_OG_SETTLED_CACHE_CONTROL;
  if (["queued", "upcoming", "scheduled", "pending"].includes(normalized)) return MARKET_OG_UPCOMING_CACHE_CONTROL;
  return MARKET_OG_LIVE_CACHE_CONTROL;
}

export function summarizeMarketOgState({ market, now = new Date() } = {}) {
  const status = resolveMarketStatus(market, now);
  const statusLabel =
    status === "closed" ? "FINAL MARKET" : status === "upcoming" ? "STARTING SOON" : "LIVE MARKET";
  const paymentToken = resolveMarketPaymentToken(market);
  const tokenSymbol = paymentToken.symbol;
  const totalTokenLabel = `${formatOgTokenAmount({
    value: marketField(market, "totalVotes", "total_votes", "totalSol", "total_sol"),
    units: marketField(market, "totalVoteUnits", "total_vote_units", "totalTokenUnits", "total_token_units"),
    decimals: paymentToken.decimals
  })} ${tokenSymbol}`;
  return {
    status,
    statusLabel,
    totalSolLabel: totalTokenLabel,
    totalTokenLabel,
    tokenSymbol,
    beginsLabel: formatWindowLabel("Begins", marketField(market, "startTime", "start_time", "startsAt", "starts_at", "startAt", "start_at")),
    endsLabel: formatWindowLabel("Ends", marketField(market, "endTime", "end_time", "endsAt", "ends_at", "endAt", "end_at")),
    leadingLabel: formatLeadingLabel(market?.options || []),
    asOfLabel: `As of ${formatUtcTime(now)} UTC`
  };
}

export async function buildMarketOgSourceHash({ market, rendererVersion = MARKET_OG_RENDERER_VERSION }) {
  const source = JSON.stringify({
    rendererVersion: normalizeRendererVersion(rendererVersion),
    id: marketField(market, "id"),
    title: marketField(market, "title"),
    summary: marketField(market, "summary"),
    imageUrl: marketField(market, "imageUrl", "image_url"),
    xUrl: marketField(market, "xUrl", "x_url"),
    websiteUrl: marketField(market, "websiteUrl", "website_url"),
    status: marketField(market, "status"),
    startAt: marketField(market, "startAt", "start_at", "startTime", "start_time", "startsAt", "starts_at"),
    endAt: marketField(market, "endAt", "end_at", "endTime", "end_time", "endsAt", "ends_at"),
    totalVotes: marketField(market, "totalVotes", "total_votes", "totalSol", "total_sol"),
    paymentTokenId: marketField(market, "paymentTokenId", "payment_token_id"),
    paymentTokenSymbol: resolveMarketPaymentTokenSymbol(market),
    paymentTokenMint: marketField(market, "paymentTokenMint", "payment_token_mint"),
    options: normalizeHashOptions(market?.options)
  });
  const digest = await crypto.subtle.digest("SHA-256", textEncoder.encode(source));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function buildMarketOgAltText(market, { serviceName = defaultServiceName } = {}) {
  return `${serviceName || defaultServiceName} market preview for ${marketField(market, "title") || "market"}`;
}

export function resolveMarketOgPublicOrigin(env = {}) {
  const raw = String(env.PM_MARKET_IMAGES_PUBLIC_ORIGIN || "").trim();
  if (!raw) {
    throw new Error("PM_MARKET_IMAGES_PUBLIC_ORIGIN is required.");
  }

  try {
    const url = new URL(raw);
    if (url.protocol !== "https:") {
      throw new Error("non-https origin");
    }
    return url.origin;
  } catch {
    throw new Error("PM_MARKET_IMAGES_PUBLIC_ORIGIN must be a valid https origin.");
  }
}

function normalizeSchema(schema) {
  const value = String(schema || "").trim();
  if (value !== "public" && value !== "dev") {
    throw new Error("Market OG schema must be public or dev.");
  }
  return value;
}

function normalizeRendererVersion(rendererVersion) {
  const value = String(rendererVersion || "").trim();
  if (!/^v[0-9]+(?:-[A-Za-z0-9_-]+)*$/.test(value)) {
    throw new Error("Market OG renderer version is invalid.");
  }
  return value;
}

function normalizeMarketId(marketId) {
  const safeMarketId = String(marketId || "").trim();
  if (!safeMarketId) throw new Error("Market OG route requires a market id.");
  if (!/^[A-Za-z0-9_-]+(?:-[A-Za-z0-9_-]+)*$/.test(safeMarketId)) {
    throw new Error("Market OG route market id contains unsupported characters.");
  }
  return safeMarketId;
}

function resolveMarketStatus(market, now) {
  const explicit = String(marketField(market, "status") || "").toLowerCase();
  if (["closed", "ended", "settled", "final"].includes(explicit)) return "closed";
  if (["queued", "upcoming", "scheduled", "pending"].includes(explicit)) return "upcoming";
  const start = parseDate(marketField(market, "startTime", "start_time", "startsAt", "starts_at", "startAt", "start_at"));
  const end = parseDate(marketField(market, "endTime", "end_time", "endsAt", "ends_at", "endAt", "end_at"));
  const nowMs = now instanceof Date ? now.getTime() : new Date(now).getTime();
  if (Number.isFinite(start) && nowMs < start) return "upcoming";
  if (Number.isFinite(end) && nowMs >= end) return "closed";
  return "live";
}

function parseDate(value) {
  const ms = Date.parse(String(value || ""));
  return Number.isFinite(ms) ? ms : NaN;
}

function formatOgAmount(value) {
  const number = Number(value || 0);
  if (!Number.isFinite(number) || number <= 0) return "0";
  if (number < 10) return number.toFixed(2).replace(/\.?0+$/, "");
  return number.toLocaleString("en-US", { maximumFractionDigits: 1 });
}

function formatOgTokenAmount({ value, units, decimals }) {
  const exactUnits = String(units || "").trim();
  if (exactUnits) {
    try {
      return formatTokenUnits(exactUnits, decimals);
    } catch {
      // Fall through to the legacy display amount.
    }
  }
  return formatOgAmount(value);
}

function resolveMarketPaymentTokenSymbol(market) {
  return resolveMarketPaymentToken(market).symbol;
}

function resolveMarketPaymentToken(market) {
  const direct = market?.paymentToken && typeof market.paymentToken === "object" ? market.paymentToken.symbol : "";
  const symbol = String(
    direct ||
    marketField(market, "paymentTokenSymbol", "payment_token_symbol") ||
    "SOL"
  ).trim().toUpperCase() || "SOL";
  const decimalValue = market?.paymentToken?.decimals ?? marketField(market, "paymentTokenDecimals", "payment_token_decimals");
  const decimals = Number(decimalValue || 9);
  return {
    symbol,
    decimals: Number.isInteger(decimals) && decimals >= 0 ? decimals : 9
  };
}

function formatWindowLabel(prefix, value) {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return `${prefix} TBA`;
  return `${prefix} ${date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC"
  })} UTC`;
}

function formatUtcTime(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return "00:00";
  return date.toISOString().slice(11, 16);
}

function formatLeadingLabel(options) {
  const leader = [...options]
    .filter((option) => option?.label)
    .sort(compareOptionVotesDesc)[0];
  return leader ? `Leading: ${String(leader.label).trim()}` : "Leading: TBA";
}

function compareOptionVotesDesc(a, b) {
  const aUnits = optionVoteUnits(a);
  const bUnits = optionVoteUnits(b);
  if (aUnits !== null && bUnits !== null && aUnits !== bUnits) {
    return bUnits > aUnits ? 1 : -1;
  }
  const aVotes = Number(a.totalVotes || a.total_votes || a.votes || 0);
  const bVotes = Number(b.totalVotes || b.total_votes || b.votes || 0);
  return (Number.isFinite(bVotes) ? bVotes : 0) - (Number.isFinite(aVotes) ? aVotes : 0);
}

function optionVoteUnits(option) {
  const raw = String(
    option?.totalVoteUnits ??
    option?.total_vote_units ??
    option?.voteUnits ??
    option?.vote_units ??
    ""
  ).trim();
  if (!/^\d+$/.test(raw)) return null;
  return BigInt(raw);
}

function normalizeHashOptions(options) {
  return (Array.isArray(options) ? options : []).map((option) => ({
    label: marketField(option, "label"),
    totalVotes: marketField(option, "totalVotes", "total_votes", "votes")
  }));
}

function marketField(market, ...keys) {
  for (const key of keys) {
    const value = market?.[key];
    if (value !== undefined && value !== null) return String(value);
  }
  return "";
}
